import {
  Alert,
  Modal,
  ModalProps,
  Space,
  Upload,
  UploadFile,
  UploadProps,
  message
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useCreate } from '@refinedev/core';
import { useState } from 'react';

const { Dragger } = Upload;

export interface ImportCertificateModalProps {
  modalClose: () => void;
  modalProps: ModalProps;
  importType: 'regulator_delivery' | 'secretary_receipt' | 'tracking_code';
  refetch?: () => void;
}

const getModalContent = (importType: string) => {
  switch (importType) {
    case 'regulator_delivery':
      return {
        title: 'Importar entrega da reguladora',
        description: 'O arquivo .csv deve conter uma coluna de Número de registro do certificado (texto) e outra com a data que foi enviado pela reguladora (dd/mm/aaaa).',
        header: '"Número de registro";"Data de envio"'
      };
    case 'secretary_receipt':
      return {
        title: 'Importar recebimento da secretaria',
        description: 'O arquivo .csv deve conter uma coluna de Número de registro do certificado (texto) e outra com a data que foi recebido pela secretaria (dd/mm/aaaa).',
        header: '"Número de registro";"Data de recebimento"'
      };
    case 'tracking_code':
      return {
        title: 'Importar código de rastreio',
        description: 'O arquivo .csv deve conter uma coluna de Número de registro do certificado (texto), outra com o código de rastreio (texto) e outra com a data que foi postado (dd/mm/aaaa).',
        header: '"Número de registro";"Código de rastreio";"Data de postagem"'
      };
    default:
      return {
        title: 'Importar dados',
        description: 'Selecione um arquivo CSV para importação.',
        header: ''
      };
  }
};

export const ImportCertificateModal = ({
  modalProps,
  importType,
  modalClose,
  refetch
}: ImportCertificateModalProps) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const { mutateAsync } = useCreate();

  const modalContent = getModalContent(importType);

  const handleUpload = () => {
    const formData = new FormData();

    for (const file of fileList) {
      formData.append('csv_file', file as unknown as Blob);
    }
    
    formData.append('upload_type', importType);
    
    setUploading(true);
    mutateAsync(
      {
        resource: 'certificate_shipments/bulk_upsert',
        values: formData,
        successNotification: () => ({
          type: 'success',
          description: 'Sucesso!',
          message: 'Upload de arquivo realizado com sucesso'
        }),
        errorNotification: () => ({
          type: 'error',
          description: 'Ops!',
          message:
            'Não foi possível realizar o upload do arquivo. Tente novamente.'
        })
      },
      {
        onSuccess: () => {
          setFileList([]);
          refetch?.();
          modalClose();
        }
      }
    )
      .finally(() => {
        setUploading(false);
      })
      .catch(() => null);
  };

  const props: UploadProps = {
    name: 'csv_file',
    multiple: false,
    fileList,
    beforeUpload: file => {
      const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');

      if (!isCSV) {
        message.error(`${file.name} não é um arquivo CSV.`);
        return false;
      }

      setFileList([file]);
      return false;
    },
    onRemove: file => {
      const index = fileList.indexOf(file);
      const newFileList = [...fileList];
      newFileList.splice(index, 1);
      setFileList(newFileList);
    }
  };

  return (
    <Modal
      style={{ maxWidth: 412 }}
      {...modalProps}
      title={modalContent.title}
      cancelText="Cancelar"
      okText="Importar"
      onOk={handleUpload}
      className="!w-[1022px]"
      destroyOnClose
      confirmLoading={uploading}
      okButtonProps={{ disabled: fileList.length === 0 }}
    >
      <Space direction="vertical">
        <Alert
          type="info"
          showIcon
          message={
              <>
                <p>{modalContent.description}</p>
                 <b>Cabeçalho do arquivo CSV:</b>
                 <p>{modalContent.header}</p>
              <p><small><b>Importante:</b> Use ponto e vírgula (;) ou vírgula (,) como separador entre as colunas.</small></p>
            </>
          }
        />
        <Dragger {...props}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Clique ou arraste o arquivo para esta área para fazer upload
          </p>
          <p className="ant-upload-hint">
            Importar um único arquivo CSV
          </p>
        </Dragger>
      </Space>
    </Modal>
  );
};
